import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, Copy, RotateCcw, Check, Sparkles, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import needsData from '../../data/needs.json';
import feelingsData from '../../data/feelings.json';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import EbookPageFooter from '../shared/EbookPageFooter';

// Full-page Feelings & Needs Exercise - Pages 5-6 of the Belonging eBook
export default function FeelingsNeedsExercise() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSection = searchParams.get('section') === 'feelings' ? 1 : 0;
  
  const [currentSection, setCurrentSection] = useState(initialSection);
  
  // Multi-select: arrays instead of single values
  const [selectedNeeds, setSelectedNeeds] = useState([]);
  const [selectedFeelingsMet, setSelectedFeelingsMet] = useState([]);
  const [selectedFeelingsNotMet, setSelectedFeelingsNotMet] = useState([]);
  
  const [copied, setCopied] = useState(false);
  const [isHelperOpen, setIsHelperOpen] = useState(false);

  const sections = [
    { id: 'needs', title: 'Universal Human Needs', page: 5, subtitle: 'Page 5' },
    { id: 'feelings-met', title: 'Feelings When Needs Are Met', page: 6, subtitle: 'Page 6' },
    { id: 'feelings-not-met', title: 'Feelings When Needs Are NOT Met', page: 6, subtitle: 'Page 6' },
  ];

  // Format array of words with commas
  const formatList = (arr) => {
    if (!arr || arr.length === 0) return '';
    return arr.map(w => w.toLowerCase()).join(', ');
  };

  // Generate both sentences (persistent across pages)
  const generateSentences = useCallback(() => {
    const needsList = formatList(selectedNeeds);
    const feelingsMetList = formatList(selectedFeelingsMet);
    const feelingsNotMetList = formatList(selectedFeelingsNotMet);
    
    const sentences = [];
    
    // Sentence for when needs ARE met
    if (needsList && feelingsMetList) {
      sentences.push({
        type: 'met',
        text: `When my need for ${needsList} is met, I feel ${feelingsMetList}.`
      });
    } else if (needsList && currentSection === 1 && selectedFeelingsMet.length === 0) {
      sentences.push({
        type: 'met',
        text: `When my need for ${needsList} is met, I feel...`
      });
    }
    
    // Sentence for when needs are NOT met
    if (needsList && feelingsNotMetList) {
      sentences.push({
        type: 'not-met',
        text: `When my need for ${needsList} is not met, I feel ${feelingsNotMetList}.`
      });
    } else if (needsList && currentSection === 2 && selectedFeelingsNotMet.length === 0) {
      sentences.push({
        type: 'not-met',
        text: `When my need for ${needsList} is not met, I feel...`
      });
    }
    
    // If only needs selected (on needs page)
    if (needsList && sentences.length === 0) {
      sentences.push({
        type: 'partial',
        text: `My need for ${needsList}...`
      });
    }
    
    return sentences;
  }, [selectedNeeds, selectedFeelingsMet, selectedFeelingsNotMet, currentSection]);

  // Toggle selection for multi-select
  const handleWordClick = (word, type) => {
    if (type === 'need') {
      setSelectedNeeds(prev => 
        prev.includes(word) 
          ? prev.filter(w => w !== word)
          : [...prev, word]
      );
    } else if (type === 'feeling-met') {
      setSelectedFeelingsMet(prev =>
        prev.includes(word)
          ? prev.filter(w => w !== word)
          : [...prev, word]
      );
    } else if (type === 'feeling-not-met') {
      setSelectedFeelingsNotMet(prev =>
        prev.includes(word)
          ? prev.filter(w => w !== word)
          : [...prev, word]
      );
    }
  };

  const reset = () => {
    setSelectedNeeds([]);
    setSelectedFeelingsMet([]);
    setSelectedFeelingsNotMet([]);
    setCopied(false);
  };

  const copySentences = () => {
    const sentences = generateSentences();
    if (sentences.length > 0) {
      const text = sentences.map(s => s.text).join('\n');
      navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const goNext = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const goPrev = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const sentences = generateSentences();
  const feelingsWhenMet = feelingsData.needsSatisfied?.categories || [];
  const feelingsWhenNotMet = feelingsData.needsNotSatisfied?.categories || [];

  // Color palette for categories
  const categoryColors = {
    'Sustenance': 'from-amber-400 to-orange-500',
    'Safety': 'from-blue-400 to-indigo-500',
    'Love': 'from-rose-400 to-pink-500',
    'Empathy': 'from-purple-400 to-violet-500',
    'Rest, Recreation & Play': 'from-cyan-400 to-sky-500',
    'Community': 'from-emerald-400 to-green-500',
    'Creativity': 'from-yellow-400 to-amber-500',
    'Autonomy': 'from-teal-400 to-emerald-500',
    'Meaning & Purpose': 'from-fuchsia-400 to-purple-500',
  };

  const getCategoryColor = (name) => categoryColors[name] || 'from-slate-400 to-slate-500';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white" data-testid="feelings-needs-exercise">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/belonging-ebook')}
              variant="ghost"
              className="text-slate-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to eBook
            </Button>
            <div className="h-6 w-px bg-slate-700"></div>
            <div>
              <h1 className="text-lg font-bold text-white">Feelings & Needs Exercise</h1>
              <p className="text-xs text-slate-400">Pages 5-6 of the Belonging eBook</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setIsHelperOpen(true)}
              className="bg-rose-600/80 hover:bg-rose-500 text-white text-sm"
            >
              <Heart className="h-4 w-4 mr-1" />
              Quick Tool
            </Button>
            <span className="text-slate-500 text-sm">
              {currentSection + 1} / {sections.length}
            </span>
          </div>
        </div>
      </header>

      {/* Sentence Builder Output - Persistent across all pages */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-b border-emerald-500/30 py-4 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {sentences.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-3">
                <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
                <span className="text-amber-400 text-xs font-medium uppercase tracking-wider">Your Expressions</span>
                <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
              </div>
              <div className="space-y-2">
                {sentences.map((s, idx) => (
                  <p key={idx} className={`text-lg md:text-xl font-light leading-relaxed tracking-wide ${
                    s.type === 'met' ? 'text-emerald-300' : 
                    s.type === 'not-met' ? 'text-rose-300' : 
                    'text-white'
                  }`}>
                    {s.text}
                  </p>
                ))}
              </div>
              <div className="flex items-center justify-center gap-3 pt-1">
                <Button
                  onClick={copySentences}
                  variant="outline"
                  size="sm"
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy All'}
                </Button>
                <Button
                  onClick={reset}
                  variant="outline"
                  size="sm"
                  className="border-slate-500/50 text-slate-400 hover:bg-slate-500/20"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <p className="text-slate-400 text-base">Select words below to build your expression</p>
              <p className="text-slate-500 text-xs">Click multiple needs and feelings (comma-separated) to create rich sentences</p>
            </div>
          )}
        </div>
      </div>

      {/* Section Title */}
      <div className="py-4 text-center">
        <h2 className={`text-2xl font-bold mb-1 ${
          currentSection === 0 ? 'text-emerald-400' : 
          currentSection === 1 ? 'text-emerald-400' : 
          'text-rose-400'
        }`}>
          {sections[currentSection].title}
        </h2>
        <p className="text-slate-400 text-sm">{sections[currentSection].subtitle} • Click multiple words to select</p>
      </div>

      {/* Main Content Area - Two Column Layout */}
      <div className="max-w-7xl mx-auto px-4 pb-24">
        {/* SECTION 0: Needs - Two Columns */}
        {currentSection === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn">
            {needsData.categories.map((category) => (
              <div key={category.name} className="group bg-slate-800/30 rounded-xl p-4 border border-slate-700/30">
                {/* Category Title - Clickable */}
                <button
                  onClick={() => handleWordClick(category.name, 'need')}
                  className={`w-full text-left mb-3 px-3 py-2 rounded-lg transition-all ${
                    selectedNeeds.includes(category.name)
                      ? `bg-gradient-to-r ${getCategoryColor(category.name)} text-white shadow-lg`
                      : 'bg-slate-700/40 text-slate-300 hover:bg-slate-700/60 border border-slate-600/50'
                  }`}
                  data-testid={`category-${category.name}`}
                >
                  <span className="font-bold text-base">{category.name}</span>
                  {selectedNeeds.includes(category.name) && (
                    <span className="ml-2 text-white/80">✓</span>
                  )}
                </button>
                
                {/* Words - Flow naturally */}
                <div className="flex flex-wrap gap-1.5">
                  {category.needs.map((need) => (
                    <button
                      key={need}
                      onClick={() => handleWordClick(need, 'need')}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        selectedNeeds.includes(need)
                          ? `bg-gradient-to-r ${getCategoryColor(category.name)} text-white shadow-md ring-2 ring-white/30`
                          : 'bg-slate-700/60 text-slate-300 hover:text-white hover:bg-slate-600 border border-slate-600/50'
                      }`}
                      data-testid={`need-${need}`}
                    >
                      {need}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Nine Universal Needs Infographic */}
            <div className="col-span-1 md:col-span-2 mt-6">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 p-4 md:p-6 shadow-xl" data-testid="nine-needs-infographic">
                {/* Animated background effects */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 left-0 w-48 h-48 bg-white rounded-full filter blur-3xl animate-pulse" style={{ animationDuration: '4s' }}></div>
                  <div className="absolute bottom-0 right-0 w-40 h-40 bg-emerald-300 rounded-full filter blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }}></div>
                  <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-teal-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }}></div>
                </div>
                
                {/* Header */}
                <div className="relative text-center mb-5">
                  <div className="inline-block">
                    <span className="text-3xl mb-2 block">🌳</span>
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">
                      The Nine Basic Human Needs
                    </h2>
                    <p className="text-emerald-200 text-sm md:text-base max-w-xl mx-auto">
                      Universal needs shared by all humans — the foundation of compassionate connection
                    </p>
                  </div>
                </div>
                
                {/* The Nine Needs Grid */}
                <div className="relative grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl mx-auto">
                  
                  {/* 1. Sustenance */}
                  <button
                    onClick={() => handleWordClick('Sustenance', 'need')}
                    className={`group relative bg-gradient-to-br from-amber-500/20 to-orange-600/20 backdrop-blur-sm border ${selectedNeeds.includes('Sustenance') ? 'border-amber-300 ring-2 ring-amber-400/50' : 'border-amber-400/50'} rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/20 cursor-pointer text-left`}
                  >
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">1</div>
                    <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🍞</div>
                    <h3 className="text-base font-bold text-amber-200 mb-1">Sustenance</h3>
                    <p className="text-amber-100/80 text-xs leading-relaxed">Food, water, air, shelter — physical foundations</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="px-1.5 py-0.5 bg-amber-500/30 rounded-full text-[10px] text-amber-200">nourishment</span>
                      <span className="px-1.5 py-0.5 bg-amber-500/30 rounded-full text-[10px] text-amber-200">health</span>
                    </div>
                  </button>
                  
                  {/* 2. Safety */}
                  <button
                    onClick={() => handleWordClick('Safety', 'need')}
                    className={`group relative bg-gradient-to-br from-blue-500/20 to-indigo-600/20 backdrop-blur-sm border ${selectedNeeds.includes('Safety') ? 'border-blue-300 ring-2 ring-blue-400/50' : 'border-blue-400/50'} rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/20 cursor-pointer text-left`}
                  >
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">2</div>
                    <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🛡️</div>
                    <h3 className="text-base font-bold text-blue-200 mb-1">Safety</h3>
                    <p className="text-blue-100/80 text-xs leading-relaxed">Protection, security, stability</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="px-1.5 py-0.5 bg-blue-500/30 rounded-full text-[10px] text-blue-200">security</span>
                      <span className="px-1.5 py-0.5 bg-blue-500/30 rounded-full text-[10px] text-blue-200">trust</span>
                    </div>
                  </button>
                  
                  {/* 3. Love */}
                  <button
                    onClick={() => handleWordClick('Love', 'need')}
                    className={`group relative bg-gradient-to-br from-rose-500/20 to-pink-600/20 backdrop-blur-sm border ${selectedNeeds.includes('Love') ? 'border-rose-300 ring-2 ring-rose-400/50' : 'border-rose-400/50'} rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-rose-500/20 cursor-pointer text-left`}
                  >
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">3</div>
                    <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">💗</div>
                    <h3 className="text-base font-bold text-rose-200 mb-1">Love</h3>
                    <p className="text-rose-100/80 text-xs leading-relaxed">Affection, intimacy, care</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="px-1.5 py-0.5 bg-rose-500/30 rounded-full text-[10px] text-rose-200">connection</span>
                      <span className="px-1.5 py-0.5 bg-rose-500/30 rounded-full text-[10px] text-rose-200">intimacy</span>
                    </div>
                  </button>
                  
                  {/* 4. Empathy */}
                  <button
                    onClick={() => handleWordClick('Empathy', 'need')}
                    className={`group relative bg-gradient-to-br from-purple-500/20 to-violet-600/20 backdrop-blur-sm border ${selectedNeeds.includes('Empathy') ? 'border-purple-300 ring-2 ring-purple-400/50' : 'border-purple-400/50'} rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20 cursor-pointer text-left`}
                  >
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-purple-400 to-violet-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">4</div>
                    <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🤝</div>
                    <h3 className="text-base font-bold text-purple-200 mb-1">Empathy</h3>
                    <p className="text-purple-100/80 text-xs leading-relaxed">Understanding, being heard, respect</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="px-1.5 py-0.5 bg-purple-500/30 rounded-full text-[10px] text-purple-200">understanding</span>
                      <span className="px-1.5 py-0.5 bg-purple-500/30 rounded-full text-[10px] text-purple-200">heard</span>
                    </div>
                  </button>
                  
                  {/* 5. Rest, Recreation & Play */}
                  <button
                    onClick={() => handleWordClick('Rest, Recreation & Play', 'need')}
                    className={`group relative bg-gradient-to-br from-cyan-500/20 to-sky-600/20 backdrop-blur-sm border ${selectedNeeds.includes('Rest, Recreation & Play') ? 'border-cyan-300 ring-2 ring-cyan-400/50' : 'border-cyan-400/50'} rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20 cursor-pointer text-left`}
                  >
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-cyan-400 to-sky-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">5</div>
                    <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🎮</div>
                    <h3 className="text-base font-bold text-cyan-200 mb-1">Rest & Play</h3>
                    <p className="text-cyan-100/80 text-xs leading-relaxed">Relaxation, fun, rejuvenation</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="px-1.5 py-0.5 bg-cyan-500/30 rounded-full text-[10px] text-cyan-200">relaxation</span>
                      <span className="px-1.5 py-0.5 bg-cyan-500/30 rounded-full text-[10px] text-cyan-200">fun</span>
                    </div>
                  </button>
                  
                  {/* 6. Community */}
                  <button
                    onClick={() => handleWordClick('Community', 'need')}
                    className={`group relative bg-gradient-to-br from-green-500/20 to-emerald-600/20 backdrop-blur-sm border ${selectedNeeds.includes('Community') ? 'border-green-300 ring-2 ring-green-400/50' : 'border-green-400/50'} rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-green-500/20 cursor-pointer text-left`}
                  >
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">6</div>
                    <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🏘️</div>
                    <h3 className="text-base font-bold text-green-200 mb-1">Community</h3>
                    <p className="text-green-100/80 text-xs leading-relaxed">Belonging, fellowship, contribution</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="px-1.5 py-0.5 bg-green-500/30 rounded-full text-[10px] text-green-200">belonging</span>
                      <span className="px-1.5 py-0.5 bg-green-500/30 rounded-full text-[10px] text-green-200">support</span>
                    </div>
                  </button>
                  
                  {/* 7. Creativity */}
                  <button
                    onClick={() => handleWordClick('Creativity', 'need')}
                    className={`group relative bg-gradient-to-br from-yellow-500/20 to-amber-600/20 backdrop-blur-sm border ${selectedNeeds.includes('Creativity') ? 'border-yellow-300 ring-2 ring-yellow-400/50' : 'border-yellow-400/50'} rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-yellow-500/20 cursor-pointer text-left`}
                  >
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">7</div>
                    <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🎨</div>
                    <h3 className="text-base font-bold text-yellow-200 mb-1">Creativity</h3>
                    <p className="text-yellow-100/80 text-xs leading-relaxed">Expression, growth, learning</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="px-1.5 py-0.5 bg-yellow-500/30 rounded-full text-[10px] text-yellow-200">expression</span>
                      <span className="px-1.5 py-0.5 bg-yellow-500/30 rounded-full text-[10px] text-yellow-200">growth</span>
                    </div>
                  </button>
                  
                  {/* 8. Autonomy */}
                  <button
                    onClick={() => handleWordClick('Autonomy', 'need')}
                    className={`group relative bg-gradient-to-br from-teal-500/20 to-green-600/20 backdrop-blur-sm border ${selectedNeeds.includes('Autonomy') ? 'border-teal-300 ring-2 ring-teal-400/50' : 'border-teal-400/50'} rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-teal-500/20 cursor-pointer text-left`}
                  >
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-teal-400 to-green-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">8</div>
                    <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🦅</div>
                    <h3 className="text-base font-bold text-teal-200 mb-1">Autonomy</h3>
                    <p className="text-teal-100/80 text-xs leading-relaxed">Freedom, choice, independence</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="px-1.5 py-0.5 bg-teal-500/30 rounded-full text-[10px] text-teal-200">freedom</span>
                      <span className="px-1.5 py-0.5 bg-teal-500/30 rounded-full text-[10px] text-teal-200">choice</span>
                    </div>
                  </button>
                  
                  {/* 9. Meaning & Purpose */}
                  <button
                    onClick={() => handleWordClick('Meaning & Purpose', 'need')}
                    className={`group relative bg-gradient-to-br from-fuchsia-500/20 to-purple-600/20 backdrop-blur-sm border ${selectedNeeds.includes('Meaning & Purpose') ? 'border-fuchsia-300 ring-2 ring-fuchsia-400/50' : 'border-fuchsia-400/50'} rounded-xl p-3 transform transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-fuchsia-500/20 cursor-pointer text-left`}
                  >
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-gradient-to-br from-fuchsia-400 to-purple-500 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md transform group-hover:rotate-12 transition-transform">9</div>
                    <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform">🎯</div>
                    <h3 className="text-base font-bold text-fuchsia-200 mb-1">Meaning & Purpose</h3>
                    <p className="text-fuchsia-100/80 text-xs leading-relaxed">Purpose, contribution, legacy</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="px-1.5 py-0.5 bg-fuchsia-500/30 rounded-full text-[10px] text-fuchsia-200">purpose</span>
                      <span className="px-1.5 py-0.5 bg-fuchsia-500/30 rounded-full text-[10px] text-fuchsia-200">legacy</span>
                    </div>
                  </button>
                </div>
                
                {/* Bottom wisdom quote */}
                <div className="relative mt-5 text-center">
                  <div className="inline-block bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 max-w-xl">
                    <p className="text-emerald-100 text-sm italic leading-relaxed">
                      "When any of these nine basic needs are unmet, we experience difficult feelings. When they are met, we flourish."
                    </p>
                    <p className="text-emerald-300 mt-2 font-semibold text-xs">— The Belonging Sessions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 1: Feelings When Met - Two Columns */}
        {currentSection === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn">
            {feelingsWhenMet.map((category) => (
              <div key={category.name} className="group bg-emerald-900/20 rounded-xl p-4 border border-emerald-700/30">
                {/* Category Title - Clickable */}
                <button
                  onClick={() => handleWordClick(category.name, 'feeling-met')}
                  className={`w-full text-left mb-3 px-3 py-2 rounded-lg transition-all ${
                    selectedFeelingsMet.includes(category.name)
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
                      : 'bg-emerald-800/40 text-emerald-300 hover:bg-emerald-800/60 border border-emerald-600/50'
                  }`}
                  data-testid={`feeling-category-${category.name}`}
                >
                  <span className="font-bold text-base uppercase tracking-wide">{category.name}</span>
                  {selectedFeelingsMet.includes(category.name) && (
                    <span className="ml-2 text-white/80">✓</span>
                  )}
                </button>
                
                {/* Feeling Words */}
                <div className="flex flex-wrap gap-1.5">
                  {category.feelings.map((feeling) => (
                    <button
                      key={feeling}
                      onClick={() => handleWordClick(feeling, 'feeling-met')}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        selectedFeelingsMet.includes(feeling)
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md ring-2 ring-white/30'
                          : 'bg-emerald-800/60 text-emerald-200 hover:text-white hover:bg-emerald-700 border border-emerald-600/50'
                      }`}
                      data-testid={`feeling-${feeling}`}
                    >
                      {feeling}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SECTION 2: Feelings When NOT Met - Two Columns */}
        {currentSection === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn">
            {feelingsWhenNotMet.map((category) => (
              <div key={category.name} className="group bg-rose-900/20 rounded-xl p-4 border border-rose-700/30">
                {/* Category Title - Clickable */}
                <button
                  onClick={() => handleWordClick(category.name, 'feeling-not-met')}
                  className={`w-full text-left mb-3 px-3 py-2 rounded-lg transition-all ${
                    selectedFeelingsNotMet.includes(category.name)
                      ? 'bg-gradient-to-r from-rose-500 to-red-500 text-white shadow-lg'
                      : 'bg-rose-800/40 text-rose-300 hover:bg-rose-800/60 border border-rose-600/50'
                  }`}
                  data-testid={`feeling-category-${category.name}`}
                >
                  <span className="font-bold text-base uppercase tracking-wide">{category.name}</span>
                  {selectedFeelingsNotMet.includes(category.name) && (
                    <span className="ml-2 text-white/80">✓</span>
                  )}
                </button>
                
                {/* Feeling Words */}
                <div className="flex flex-wrap gap-1.5">
                  {category.feelings.map((feeling) => (
                    <button
                      key={feeling}
                      onClick={() => handleWordClick(feeling, 'feeling-not-met')}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        selectedFeelingsNotMet.includes(feeling)
                          ? 'bg-gradient-to-r from-rose-500 to-red-500 text-white shadow-md ring-2 ring-white/30'
                          : 'bg-rose-800/60 text-rose-200 hover:text-white hover:bg-rose-700 border border-rose-600/50'
                      }`}
                      data-testid={`feeling-${feeling}`}
                    >
                      {feeling}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Practice Scenario & Contact Form */}
        <EbookPageFooter 
          pageContext="Feelings & Needs Exercise"
          pageNumber={5}
          showScenario={true}
          showContactForm={true}
        />
      </div>

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700/50 py-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            onClick={goPrev}
            disabled={currentSection === 0}
            variant="outline"
            className={`${currentSection === 0 ? 'opacity-30' : ''} border-slate-600 text-slate-300 hover:bg-slate-700`}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {sections.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSection(idx)}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentSection
                    ? idx === 2 ? 'bg-rose-500 scale-125' : 'bg-emerald-500 scale-125'
                    : 'bg-slate-600 hover:bg-slate-500'
                }`}
                data-testid={`nav-dot-${idx}`}
              />
            ))}
          </div>

          <Button
            onClick={goNext}
            disabled={currentSection === sections.length - 1}
            className={`${currentSection === sections.length - 1 ? 'opacity-30' : ''} ${
              currentSection === 1 ? 'bg-rose-600 hover:bg-rose-500' : 'bg-emerald-600 hover:bg-emerald-500'
            } text-white`}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Feelings & Needs Helper Popup */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
      />
    </div>
  );
}
